// AdminLernoOrders.js — LERNO CO print-on-demand order admin.
// Super-admin only. One page: order table + new-order form.
import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const GRADES = ["K", "1", "2", "3-4", "5-6", "7-8", "9-12"];
const MODALITIES = ["listening", "reading", "speaking", "writing"];
const TIERS = ["A", "BC"];
const LANGS = [
  { code: "es", label: "Spanish" },
  { code: "zh", label: "Chinese" },
  { code: "bn", label: "Bengali" },
  { code: "ar", label: "Arabic" },
];

// First-class order-type taxonomy — mirror of OrderType in
// backend/models/lerno_order.py. If you add a type here, add it there.
const ORDER_TYPES = [
  { v: "paid",          label: "Paid",          hint: "Standard NYC DOE P.O. — fully billable" },
  { v: "complimentary", label: "Complimentary", hint: "$0 goodwill drop — no PO required, invoice stamps NO CHARGE" },
  { v: "demo",          label: "Demo",          hint: "Short-term prospect eval — same $0 stamp, tracked for conversion" },
  { v: "internal_qa",   label: "Internal / QA", hint: "Ops bench build — excluded from every customer-facing report" },
];
const COMP_REASONS = [
  { v: "demo",              label: "Demo"                     },
  { v: "vip_eval",          label: "VIP evaluation"           },
  { v: "replacement",       label: "Replacement for an issue" },
  { v: "marketing",         label: "Marketing / outreach"     },
  { v: "internal_qa",       label: "Internal QA / testing"    },
  { v: "promotional_pitch", label: "Promotional pitch"        },
  { v: "other",             label: "Other (see admin notes)"  },
];

const emptyOrder = () => ({
  order_type: "paid", comp_reason: "", comp_approved_by: "",
  po_number: "", school_name: "", school_dbn: "",
  ell_coordinator_name: "", ell_coordinator_email: "",
  additional_recipient_emails: "", ship_to_address: "",
  bundle_preset: "single-grade", copies: 30,
  grades: [], modalities: [...MODALITIES], tiers: [...TIERS], admin_notes: "",
  preferred_home_languages: [],
});

function authHeader() {
  const tok = localStorage.getItem("session_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function AdminLernoOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presets, setPresets] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyOrder());
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  // UI-only filter: 'all' | 'billable' | 'non_billable' | one of the OrderType enum values.
  const [typeFilter, setTypeFilter] = useState("all");
  const [compStats, setCompStats] = useState(null);

  const reload = () => {
    setLoading(true);
    const url = typeFilter === "all"
      ? `${API}/api/admin/lerno-orders`
      : `${API}/api/admin/lerno-orders?order_type=${encodeURIComponent(typeFilter)}`;
    axios.get(url, { headers: authHeader() })
      .then(r => setOrders(r.data.orders || []))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load orders"))
      .finally(() => setLoading(false));
    axios.get(`${API}/api/admin/lerno-orders/-/language-analytics`, { headers: authHeader() })
      .then(r => setAnalytics(r.data)).catch(() => {});
    axios.get(`${API}/api/admin/lerno-orders/-/comp-stats`, { headers: authHeader() })
      .then(r => setCompStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    reload();
    axios.get(`${API}/api/admin/lerno-orders/-/bundle-presets`, { headers: authHeader() })
      .then(r => setPresets(r.data.presets || {})).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const isComp = form.order_type !== "paid";

  const applyPreset = (p) => setForm(f => ({ ...f, bundle_preset: p, grades: (presets[p] || []).slice() }));
  const toggleArr = (key, val) => setForm(f => {
    const cur = f[key]; const has = cur.includes(val);
    return { ...f, [key]: has ? cur.filter(x => x !== val) : [...cur, val] };
  });

  // PO is only required when the order is PAID. Comp orders can leave
  // it blank — the backend auto-stamps `COMP-YYMMDD-SEQ`.
  const canSubmit = () =>
    (!isComp ? form.po_number.trim() : true) &&
    (isComp ? !!form.comp_reason : true) &&
    form.school_name.trim() &&
    form.ell_coordinator_name.trim() && form.ell_coordinator_email.trim() &&
    form.grades.length > 0 && form.modalities.length > 0 && form.tiers.length > 0 && form.copies >= 1;

  const submit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true); setFlash(null);
    try {
      const licensed_skus = form.grades.map(g => ({
        grade: g, copies: parseInt(form.copies, 10),
        modalities: form.modalities.slice(), tiers: form.tiers.slice(),
      }));
      const body = {
        order_type: form.order_type,
        comp_reason: isComp ? form.comp_reason : null,
        comp_approved_by: isComp ? (form.comp_approved_by.trim() || null) : null,
        po_number: form.po_number.trim() || null,
        school_name: form.school_name.trim(),
        school_dbn: form.school_dbn.trim() || null,
        ell_coordinator_name: form.ell_coordinator_name.trim(),
        ell_coordinator_email: form.ell_coordinator_email.trim(),
        additional_recipient_emails: form.additional_recipient_emails
          .split(/[,;\s]+/).filter(x => x && x.includes("@")),
        ship_to_address: form.ship_to_address.trim() || null,
        bundle_preset: form.bundle_preset, licensed_skus,
        admin_notes: form.admin_notes.trim() || null,
        preferred_home_languages: form.preferred_home_languages,
      };
      const created = await axios.post(`${API}/api/admin/lerno-orders`, body, { headers: authHeader() });
      const oid = created.data.order_id;
      setFlash({ kind: "info", msg: `Order ${oid} created. Building ZIP…` });
      const issued = await axios.post(`${API}/api/admin/lerno-orders/${oid}/issue`, {}, { headers: authHeader() });
      setFlash({ kind: "ok", msg: `Order ${oid} issued. Email sent: ${issued.data.email_sent ? "yes" : "no"}.` });
      setForm(emptyOrder()); setShowForm(false); reload();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Failed to create/issue order" });
    } finally { setSubmitting(false); }
  };

  const issueAgain = async (oid) => {
    if (!window.confirm(`Rebuild + re-issue ${oid}? Rotates the download token and re-emails the buyer.`)) return;
    try { await axios.post(`${API}/api/admin/lerno-orders/${oid}/issue`, {}, { headers: authHeader() }); reload(); }
    catch (e) { alert(e?.response?.data?.detail || "issue failed"); }
  };

  const previewFormQuickStart = async () => {
    if (!form.school_name.trim() || (!isComp && !form.po_number.trim())) {
      setFlash({ kind: "err", msg: "Enter School Name first (and PO # if this is a paid order) to preview the Quick Start." });
      return;
    }
    try {
      const resp = await axios.post(
        `${API}/api/admin/lerno-orders/-/preview-quick-start`,
        {
          school_name: form.school_name.trim(),
          po_number: (form.po_number.trim() || "COMP-PREVIEW"),
          school_dbn: form.school_dbn.trim() || null,
          ell_coordinator_name: form.ell_coordinator_name.trim() || "ELL Coordinator",
          ell_coordinator_email: form.ell_coordinator_email.trim() || "ell@school.example",
        },
        { headers: authHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setFlash({ kind: "err", msg: "Preview failed: " + (e?.response?.data?.detail || e.message) });
    }
  };

  const previewOrderQuickStart = async (oid) => {
    try {
      const resp = await axios.get(
        `${API}/api/admin/lerno-orders/${oid}/preview-quick-start`,
        { headers: authHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert("Preview failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const previewFormBundle = async () => {
    if (!form.school_name.trim() || (!isComp && !form.po_number.trim())) {
      setFlash({ kind: "err", msg: "Enter School Name first (and PO # if this is a paid order) to preview the full bundle." });
      return;
    }
    try {
      const resp = await axios.post(
        `${API}/api/admin/lerno-orders/-/preview-bundle`,
        {
          school_name: form.school_name.trim(),
          po_number: (form.po_number.trim() || "COMP-PREVIEW"),
          school_dbn: form.school_dbn.trim() || null,
          ell_coordinator_name: form.ell_coordinator_name.trim() || "ELL Coordinator",
          ell_coordinator_email: form.ell_coordinator_email.trim() || "ell@school.example",
        },
        { headers: authHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setFlash({ kind: "err", msg: "Bundle preview failed: " + (e?.response?.data?.detail || e.message) });
    }
  };

  const previewOrderBundle = async (oid) => {
    try {
      const resp = await axios.get(
        `${API}/api/admin/lerno-orders/${oid}/preview-bundle`,
        { headers: authHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert("Bundle preview failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const resendEmail = async (oid, currentEmail) => {
    const to_email = window.prompt(
      "Resend the download link to which email address?\n"
      + "(This does NOT change the coordinator email on the order.)",
      currentEmail || "",
    );
    if (!to_email || !to_email.includes("@")) return;
    try {
      const r = await axios.post(
        `${API}/api/admin/lerno-orders/${oid}/resend-email`,
        { to_email },
        { headers: authHeader() },
      );
      setFlash({
        kind: r.data.email_sent ? "ok" : "err",
        msg: r.data.email_sent
          ? `Resent link to ${to_email}.`
          : `Resend to ${to_email} failed (SendGrid).`,
      });
      reload();
    } catch (e) {
      alert("Resend failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const updateCoordinator = async (oid, currentEmail, currentName) => {
    const new_email = window.prompt(
      "New coordinator email? (This becomes the permanent contact and clears the bounce flag.)",
      currentEmail || "",
    );
    if (!new_email || !new_email.includes("@")) return;
    const new_name = window.prompt(
      "New coordinator name? (Leave blank to keep the existing one.)",
      currentName || "",
    );
    try {
      await axios.patch(
        `${API}/api/admin/lerno-orders/${oid}/update-coordinator`,
        {
          ell_coordinator_email: new_email,
          ell_coordinator_name: (new_name && new_name.trim()) || null,
        },
        { headers: authHeader() },
      );
      setFlash({ kind: "ok", msg: `Coordinator email updated to ${new_email}. Bounce flag cleared.` });
      reload();
    } catch (e) {
      alert("Update failed: " + (e?.response?.data?.detail || e.message));
    }
  };
  const revoke = async (oid) => {
    const reason = window.prompt("Reason for revoking?");
    if (!reason) return;
    try { await axios.post(`${API}/api/admin/lerno-orders/${oid}/revoke`, { reason }, { headers: authHeader() }); reload(); }
    catch (e) { alert(e?.response?.data?.detail || "revoke failed"); }
  };
  const extend = async (oid) => {
    const dateStr = window.prompt("New expiry date (YYYY-MM-DD)?");
    if (!dateStr) return;
    const reason = window.prompt("Reason for extension?") || "Complimentary extension";
    try {
      await axios.post(`${API}/api/admin/lerno-orders/${oid}/extend`,
        { new_expiry_iso: `${dateStr}T23:59:59+00:00`, reason }, { headers: authHeader() }); reload();
    } catch (e) { alert(e?.response?.data?.detail || "extend failed"); }
  };
  const publicLink = (token) => `${window.location.origin}/download/${token}`;

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px",
      fontFamily: "-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", color: "#0F172A" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>LERNO CO — Print-on-Demand Orders</h1>
          <div style={{ color: "#64748B", fontSize: 13, marginTop: 4 }}>
            Manual order entry for NYC school POs · single-school academic-year license
          </div>
        </div>
        <button data-testid="lerno-new-order-btn" onClick={() => setShowForm(!showForm)}
          style={{ padding: "10px 20px", background: showForm ? "#64748B" : "#1E293B",
            color: "#FFF", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
          {showForm ? "Cancel" : "+ New Order"}
        </button>
        <button data-testid="lerno-test-order-btn"
          onClick={() => {
            const ts = new Date().toISOString().slice(0,19).replace(/[T:]/g,'-');
            setForm({
              ...emptyOrder(),
              po_number: `TEST-${ts}`,
              school_name: "LERNO CO — Internal Test",
              school_dbn: "TEST",
              ell_coordinator_name: "LERNO Admin",
              ell_coordinator_email: prompt("Send test to email:", "admin@lernoco.com") || "admin@lernoco.com",
              bundle_preset: "single-grade",
              copies: 1,
              grades: ["K"],
              modalities: ["reading"],
              tiers: ["A"],
              admin_notes: "Internal test order — safe to delete.",
            });
            setShowForm(true);
          }}
          style={{ marginLeft: 8, padding: "10px 16px", background: "#F1F5F9",
            color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8,
            fontWeight: 500, cursor: "pointer", fontSize: 13 }}>
          Fill Test Order
        </button>
      </div>

      {flash && (
        <div style={{ padding: "12px 16px", marginBottom: 16, borderRadius: 6,
          background: flash.kind === "err" ? "#FEF2F2" : flash.kind === "ok" ? "#F0FDF4" : "#F0F9FF",
          color: flash.kind === "err" ? "#7F1D1D" : flash.kind === "ok" ? "#166534" : "#1E40AF", fontSize: 14 }}>
          {flash.msg}
        </div>
      )}

      {analytics && (
        <div data-testid="lerno-analytics"
          style={{ background: "#FFF", borderRadius: 12, padding: "14px 18px",
            marginBottom: 16, boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
            display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: "#64748B" }}>
              Home-language demand
            </div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              {analytics.orders_with_language_flag}/{analytics.total_orders} orders flagged a primary need
            </div>
          </div>
          {LANGS.map(l => {
            const count = analytics.counts?.[l.code] || 0;
            const denom = analytics.orders_with_language_flag || 1;
            const pct = Math.round((count / denom) * 100);
            return (
              <div key={l.code} data-testid={`lerno-analytics-${l.code}`}
                style={{ minWidth: 130 }}>
                <div style={{ fontSize: 12, color: "#334155", fontWeight: 600 }}>
                  {l.label}
                  <span style={{ color: "#94A3B8", fontWeight: 400, marginLeft: 6 }}>({count})</span>
                </div>
                <div style={{ background: "#E2E8F0", height: 6, borderRadius: 3, overflow: "hidden", marginTop: 4 }}>
                  <div style={{ background: "#1E293B", height: 6, width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div style={{ background: "#FFF", borderRadius: 12, padding: 24, boxShadow: "0 1px 3px rgba(15,23,42,0.08)", marginBottom: 24 }}>
          {/* ─── Order type pill-row + optional comp reason ─── */}
          <div style={{ marginBottom: 20 }}>
            <Label>Order type</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              {ORDER_TYPES.map(t => (
                <button
                  key={t.v}
                  data-testid={`lerno-ordertype-${t.v}`}
                  onClick={() => setForm(f => ({ ...f, order_type: t.v,
                    comp_reason: t.v === "paid" ? "" : f.comp_reason }))}
                  title={t.hint}
                  style={{
                    padding: "8px 14px", borderRadius: 999,
                    border: "1.5px solid " + (form.order_type === t.v ? "#1E293B" : "#CBD5E1"),
                    background: form.order_type === t.v
                      ? (t.v === "paid" ? "#1E293B" : "#F59E0B")
                      : "#FFF",
                    color: form.order_type === t.v
                      ? (t.v === "paid" ? "#FFF" : "#0F172A")
                      : "#0F172A",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>
              {ORDER_TYPES.find(t => t.v === form.order_type)?.hint}
            </div>
          </div>

          {isComp && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20,
              background: "#FFFBEB", padding: 14, borderRadius: 10, border: "1px solid #FDE68A" }}>
              <div>
                <Label>Comp reason <span style={{ color: "#DC2626" }}>*</span></Label>
                <select
                  data-testid="lerno-comp-reason"
                  value={form.comp_reason}
                  onChange={e => setForm({ ...form, comp_reason: e.target.value })}
                  style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 6,
                    border: "1px solid #CBD5E1", fontSize: 14, background: "#FFF" }}>
                  <option value="">— select a reason —</option>
                  {COMP_REASONS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
              </div>
              <Field
                label="Approved by (email, optional)"
                testid="lerno-comp-approved-by"
                value={form.comp_approved_by}
                onChange={v => setForm({ ...form, comp_approved_by: v })}
                type="email"
              />
              <div style={{ gridColumn: "1 / -1", fontSize: 11, color: "#92400E" }}>
                <b>No charge.</b> PO # is optional — if left blank the system auto-stamps a
                sortable <code>COMP-YYMMDD-XXXX</code> reference. Invoice PDF prints
                "COMPLIMENTARY · NO CHARGE · $0.00". Reps earn zero commission.
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <Field
              label={isComp ? "PO # (optional)" : "PO #"}
              testid="lerno-po"
              value={form.po_number}
              onChange={v => setForm({ ...form, po_number: v })}
            />
            <Field label="School name" testid="lerno-school" value={form.school_name} onChange={v => setForm({ ...form, school_name: v })} />
            <Field label="DBN (optional)" testid="lerno-dbn" value={form.school_dbn} onChange={v => setForm({ ...form, school_dbn: v })} />
            <Field label="ELL Coordinator name" testid="lerno-coord-name" value={form.ell_coordinator_name} onChange={v => setForm({ ...form, ell_coordinator_name: v })} />
            <Field label="ELL Coordinator email" testid="lerno-coord-email" value={form.ell_coordinator_email} onChange={v => setForm({ ...form, ell_coordinator_email: v })} type="email" />
            <Field label="Additional recipients (comma-sep)" testid="lerno-cc" value={form.additional_recipient_emails} onChange={v => setForm({ ...form, additional_recipient_emails: v })} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <Label>Bundle preset</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              {["K-5", "6-8", "9-12", "single-grade", "custom"].map(p => (
                <button key={p} data-testid={`lerno-preset-${p}`} onClick={() => applyPreset(p)}
                  style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid #CBD5E1",
                    background: form.bundle_preset === p ? "#1E293B" : "#FFF",
                    color: form.bundle_preset === p ? "#FFF" : "#0F172A", fontSize: 13, cursor: "pointer" }}>{p}</button>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
            <CheckboxGroup label="Grades" testid_root="lerno-grade" values={GRADES} selected={form.grades} onToggle={v => toggleArr("grades", v)} />
            <CheckboxGroup label="Modalities" testid_root="lerno-mod" values={MODALITIES} selected={form.modalities} onToggle={v => toggleArr("modalities", v)} />
            <CheckboxGroup label="Tiers" testid_root="lerno-tier" values={TIERS} selected={form.tiers} onToggle={v => toggleArr("tiers", v)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 3fr", gap: 16, marginBottom: 20 }}>
            <Field label="Print quota per grade (student copies)" testid="lerno-copies" value={form.copies} onChange={v => setForm({ ...form, copies: v })} type="number" />
            <Field label="Ship-to (optional)" testid="lerno-shipto" value={form.ship_to_address} onChange={v => setForm({ ...form, ship_to_address: v })} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <Label>Primary home languages at this school (optional)</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              {LANGS.map(l => (
                <label key={l.code} data-testid={`lerno-lang-${l.code}`}
                  style={{
                    padding: "6px 10px", borderRadius: 4, fontSize: 13, cursor: "pointer",
                    background: form.preferred_home_languages.includes(l.code) ? "#1E293B" : "#F1F5F9",
                    color: form.preferred_home_languages.includes(l.code) ? "#FFF" : "#0F172A",
                    border: "1px solid #CBD5E1",
                  }}>
                  <input type="checkbox" style={{ display: "none" }}
                    checked={form.preferred_home_languages.includes(l.code)}
                    onChange={() => toggleArr("preferred_home_languages", l.code)} />
                  {l.label}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
              All four bilingual parent letters ship in every ZIP regardless. This flag just powers language-demand analytics.
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <Label>Admin notes</Label>
            <textarea data-testid="lerno-notes" rows={2} value={form.admin_notes}
              onChange={e => setForm({ ...form, admin_notes: e.target.value })}
              style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 6, border: "1px solid #CBD5E1", fontSize: 14 }} />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, color: "#64748B" }}>
              Student BLM reproduction masters in ZIP: <b>{form.grades.length * form.modalities.length * form.tiers.length}</b>
              &nbsp;(school may print up to <b>{parseInt(form.copies, 10) || 0}</b> copies of each)
              &nbsp;·&nbsp; Teacher PDFs: <b>{form.grades.length * form.modalities.length * form.tiers.length}</b>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button data-testid="lerno-preview-qs" onClick={previewFormQuickStart}
                disabled={!form.school_name.trim() || (!isComp && !form.po_number.trim())}
                title="Preview the Quick Start PDF (first page at ZIP root)."
                style={{ padding: "12px 16px",
                  background: (!form.school_name.trim() || (!isComp && !form.po_number.trim())) ? "#E2E8F0" : "#FFF",
                  color: (!form.school_name.trim() || (!isComp && !form.po_number.trim())) ? "#94A3B8" : "#1E293B",
                  border: "1px solid #1E293B", borderRadius: 8, fontWeight: 600,
                  cursor: (form.school_name.trim() && (isComp || form.po_number.trim())) ? "pointer" : "not-allowed" }}>
                Preview Quick Start
              </button>
              <button data-testid="lerno-preview-bundle" onClick={previewFormBundle}
                disabled={!form.school_name.trim() || (!isComp && !form.po_number.trim())}
                title="Preview Quick Start + all Additional_Resources PDFs merged into one file."
                style={{ padding: "12px 16px",
                  background: (!form.school_name.trim() || (!isComp && !form.po_number.trim())) ? "#E2E8F0" : "#FFF",
                  color: (!form.school_name.trim() || (!isComp && !form.po_number.trim())) ? "#94A3B8" : "#1E293B",
                  border: "1px solid #1E293B", borderRadius: 8, fontWeight: 600,
                  cursor: (form.school_name.trim() && (isComp || form.po_number.trim())) ? "pointer" : "not-allowed" }}>
                Preview Full Bundle
              </button>
              <button data-testid="lerno-submit-order" onClick={submit} disabled={!canSubmit() || submitting}
                style={{ padding: "12px 28px",
                  background: !canSubmit() || submitting ? "#94A3B8" : (isComp ? "#F59E0B" : "#1E293B"),
                  color: isComp ? "#0F172A" : "#FFF",
                  border: "none", borderRadius: 8, fontWeight: 700,
                  cursor: canSubmit() ? "pointer" : "not-allowed" }}>
                {submitting
                  ? "Creating + issuing…"
                  : (isComp ? "Create + Issue Comp Order ($0)" : "Create + Issue Order")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Comp Stats pill-row + Type filter ─── */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}
           data-testid="lerno-comp-stats">
        {compStats && (
          <>
            <StatPill color="#1E293B" bg="#F1F5F9"
                      label="Paid"           value={compStats.paid ?? 0} testid="lerno-stat-paid" />
            <StatPill color="#92400E" bg="#FEF3C7"
                      label="Complimentary"  value={compStats.complimentary ?? 0} testid="lerno-stat-complimentary" />
            <StatPill color="#1E3A8A" bg="#DBEAFE"
                      label="Demo"           value={compStats.demo ?? 0} testid="lerno-stat-demo" />
            <StatPill color="#065F46" bg="#D1FAE5"
                      label="Internal / QA"  value={compStats.internal_qa ?? 0} testid="lerno-stat-internal-qa" />
            <span style={{ color: "#64748B", fontSize: 12, marginLeft: 4 }}>
              <b>{compStats.non_billable_total ?? 0}</b> non-billable total
            </span>
          </>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: "#64748B" }}>Show</span>
          {[
            { v: "all",           label: "All" },
            { v: "billable",      label: "Billable only" },
            { v: "non_billable",  label: "Comps only" },
            { v: "paid",          label: "Paid" },
            { v: "complimentary", label: "Comp" },
            { v: "demo",          label: "Demo" },
            { v: "internal_qa",   label: "QA" },
          ].map(f => (
            <button key={f.v}
              data-testid={`lerno-filter-${f.v}`}
              onClick={() => setTypeFilter(f.v)}
              style={{ padding: "4px 10px", borderRadius: 999, fontSize: 12,
                background: typeFilter === f.v ? "#1E293B" : "#FFF",
                color: typeFilter === f.v ? "#FFF" : "#334155",
                border: "1px solid " + (typeFilter === f.v ? "#1E293B" : "#CBD5E1"),
                cursor: "pointer", fontWeight: 600 }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div style={{ color: "#64748B" }}>Loading orders…</div>
        : error ? <div style={{ color: "#DC2626" }}>{error}</div>
        : (
        <div style={{ background: "#FFF", borderRadius: 12, boxShadow: "0 1px 3px rgba(15,23,42,0.08)", overflow: "hidden" }} data-testid="lerno-order-table">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#F1F5F9", textAlign: "left" }}>
                <Th>Order</Th><Th>Type</Th><Th>PO / Ref</Th><Th>School</Th><Th>Grades</Th>
                <Th>Print Quota</Th><Th>Expires</Th><Th>Status</Th><Th>Link</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                  No orders yet. Click "New Order" to enter your first PO.
                </td></tr>
              ) : orders.map(o => (
                <tr key={o.order_id} style={{ borderTop: "1px solid #E2E8F0" }} data-testid={`lerno-order-row-${o.order_id}`}>
                  <Td><code style={{ fontSize: 12 }}>{o.order_id}</code></Td>
                  <Td><TypeBadge type={o.order_type || "paid"} reason={o.comp_reason} /></Td>
                  <Td>{o.po_number}</Td>
                  <Td>
                    <div>{o.school_name}</div>
                    {o.school_dbn && <div style={{ fontSize: 12, color: "#64748B" }}>{o.school_dbn}</div>}
                  </Td>
                  <Td>{[...new Set(o.licensed_skus.map(s => s.grade))].join(", ")}</Td>
                  <Td>{o.licensed_skus.reduce((sum, s) => sum + s.copies * (s.modalities?.length || 1) * (s.tiers?.length || 1), 0)}</Td>
                  <Td>{fmtDate(o.license_expires_at)}</Td>
                  <Td><StatusBadge status={o.status} />
                    {o.email_bounced_at && (
                      <div style={{ marginTop: 4 }} data-testid={`lerno-bounce-${o.order_id}`}
                           title={o.email_bounce_reason || "Delivery failed"}>
                        <span style={{ display: "inline-block", padding: "2px 6px",
                                       background: "#FEE2E2", color: "#991B1B",
                                       borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                          ⚠ BOUNCED
                        </span>
                        <div style={{ fontSize: 10, color: "#991B1B", marginTop: 2, maxWidth: 160, wordBreak: "break-all" }}>
                          {o.email_bounce_recipient || ""}
                        </div>
                        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                          <button
                            data-testid={`lerno-resend-${o.order_id}`}
                            onClick={() => resendEmail(o.order_id, o.ell_coordinator_email)}
                            style={{ padding: "2px 6px", fontSize: 10, background: "#FFF",
                                     border: "1px solid #991B1B", color: "#991B1B",
                                     borderRadius: 3, cursor: "pointer" }}>
                            Resend to…
                          </button>
                          {(o.email_bounce_recipient || "").toLowerCase() ===
                             (o.ell_coordinator_email || "").toLowerCase() && (
                            <button
                              data-testid={`lerno-update-coord-${o.order_id}`}
                              onClick={() => updateCoordinator(
                                o.order_id, o.ell_coordinator_email, o.ell_coordinator_name)}
                              style={{ padding: "2px 6px", fontSize: 10, background: "#991B1B",
                                       border: "1px solid #991B1B", color: "#FFF",
                                       borderRadius: 3, cursor: "pointer" }}>
                            Update coordinator email
                          </button>
                          )}
                        </div>
                      </div>
                    )}
                  </Td>
                  <Td>{o.download_token ? (
                    <a href={publicLink(o.download_token)} target="_blank" rel="noreferrer"
                       style={{ color: "#1D4ED8", fontSize: 12 }} data-testid={`lerno-link-${o.order_id}`}>
                      /download/{o.download_token.slice(0, 8)}…
                    </a>) : <span style={{ color: "#94A3B8", fontSize: 12 }}>—</span>}</Td>
                  <Td>
                    <button onClick={() => previewOrderQuickStart(o.order_id)} style={btnMini} data-testid={`lerno-preview-${o.order_id}`}>Preview</button>
                    <button onClick={() => previewOrderBundle(o.order_id)} style={btnMini} data-testid={`lerno-preview-bundle-${o.order_id}`}>Bundle</button>
                    {o.status !== "revoked" && (
                      <button onClick={() => issueAgain(o.order_id)} style={btnMini} data-testid={`lerno-reissue-${o.order_id}`}>Re-issue</button>
                    )}
                    {(o.status === "issued" || o.status === "extended") && (
                      <button onClick={() => extend(o.order_id)} style={btnMini} data-testid={`lerno-extend-${o.order_id}`}>Extend</button>
                    )}
                    {o.status !== "revoked" && (
                      <button onClick={() => revoke(o.order_id)} style={{ ...btnMini, color: "#991B1B" }} data-testid={`lerno-revoke-${o.order_id}`}>Revoke</button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const btnMini = { marginRight: 6, padding: "4px 10px", fontSize: 12, background: "#F1F5F9", border: "1px solid #CBD5E1", borderRadius: 4, cursor: "pointer" };
function Label({ children }) { return <label style={{ fontSize: 12, textTransform: "uppercase", color: "#64748B", letterSpacing: 0.8 }}>{children}</label>; }
function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <input data-testid={testid} type={type} value={value} onChange={e => onChange(e.target.value)}
        style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 6, border: "1px solid #CBD5E1", fontSize: 14, boxSizing: "border-box" }} />
    </div>
  );
}
function CheckboxGroup({ label, values, selected, onToggle, testid_root }) {
  return (
    <div>
      <Label>{label}</Label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
        {values.map(v => (
          <label key={v} style={{
            padding: "6px 10px", background: selected.includes(v) ? "#1E293B" : "#F1F5F9",
            color: selected.includes(v) ? "#FFF" : "#0F172A", borderRadius: 4,
            fontSize: 13, cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={selected.includes(v)} onChange={() => onToggle(v)}
              style={{ display: "none" }} data-testid={`${testid_root}-${v}`} />{v}
          </label>
        ))}
      </div>
    </div>
  );
}
function Th({ children }) { return <th style={{ padding: "10px 16px", fontSize: 12, textTransform: "uppercase", color: "#475569", letterSpacing: 0.6 }}>{children}</th>; }
function Td({ children }) { return <td style={{ padding: "10px 16px" }}>{children}</td>; }
function StatusBadge({ status }) {
  const styles = {
    issued: { bg: "#DCFCE7", fg: "#166534" }, extended: { bg: "#DCFCE7", fg: "#166534" },
    revoked: { bg: "#FEE2E2", fg: "#991B1B" }, expired: { bg: "#FEF3C7", fg: "#854D0E" },
    draft: { bg: "#F1F5F9", fg: "#334155" },
  };
  const s = styles[status] || styles.draft;
  return <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 12, background: s.bg, color: s.fg }}>{status}</span>;
}

function TypeBadge({ type, reason }) {
  const styles = {
    paid:          { bg: "#F1F5F9", fg: "#334155", label: "Paid" },
    complimentary: { bg: "#FEF3C7", fg: "#92400E", label: "Comp" },
    demo:          { bg: "#DBEAFE", fg: "#1E3A8A", label: "Demo" },
    internal_qa:   { bg: "#D1FAE5", fg: "#065F46", label: "QA" },
  };
  const s = styles[type] || styles.paid;
  return (
    <span
      title={reason ? `Reason: ${reason}` : undefined}
      data-testid={`lerno-type-${type}`}
      style={{ padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
               letterSpacing: 0.4, background: s.bg, color: s.fg,
               textTransform: "uppercase" }}>
      {s.label}
    </span>
  );
}

function StatPill({ label, value, bg, color, testid }) {
  return (
    <div data-testid={testid}
         style={{ display: "inline-flex", gap: 6, alignItems: "center",
                  background: bg, color, padding: "4px 10px",
                  borderRadius: 999, fontSize: 12 }}>
      <span style={{ fontWeight: 700 }}>{value}</span>
      <span style={{ opacity: 0.8 }}>{label}</span>
    </div>
  );
}
